import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Loader2, UserRoundCog } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/app/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { posRegisterService, type PosActor } from '@/app/services/pos.service'

interface ChangeSellerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentSeller: PosActor | null
}

export function ChangeSellerModal({
  open,
  onOpenChange,
  currentSeller,
}: ChangeSellerModalProps) {
  const queryClient = useQueryClient()
  const [sellerId, setSellerId] = useState('')

  const sellersQuery = useQuery({
    queryKey: ['pos-sellers'],
    queryFn: posRegisterService.getSellers,
    enabled: open,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (open) {
      setSellerId(currentSeller?.source_user_id?.toString() ?? currentSeller?.id?.toString() ?? '')
    }
  }, [currentSeller, open])

  const mutation = useMutation({
    mutationFn: () => posRegisterService.updateSessionSeller({
      seller_user_id: Number(sellerId),
      expected_seller_user_id: currentSeller?.source_user_id ?? currentSeller?.id ?? null,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['register-context'] })
      toast.success('Vendedor actualizado correctamente.')
      onOpenChange(false)
    },
    onError: (error: unknown) => {
      const message = isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined
      toast.error(message || 'No fue posible actualizar el vendedor.')
      void queryClient.invalidateQueries({ queryKey: ['register-context'] })
    },
  })

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !mutation.isPending && onOpenChange(nextOpen)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRoundCog className="h-5 w-5" />
            Cambiar vendedor
          </DialogTitle>
          <DialogDescription>
            Selecciona al usuario responsable de las ventas de esta sesión de caja.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="pos-session-seller">
              Vendedor
            </label>
            <Select value={sellerId} onValueChange={setSellerId} disabled={sellersQuery.isLoading || mutation.isPending}>
              <SelectTrigger id="pos-session-seller" className="w-full">
                <SelectValue placeholder={sellersQuery.isLoading ? 'Cargando usuarios...' : 'Selecciona un usuario'} />
              </SelectTrigger>
              <SelectContent>
                {(sellersQuery.data ?? []).map((seller) => (
                  <SelectItem key={seller.id} value={seller.id.toString()}>
                    {[seller.name, seller.last_name].filter(Boolean).join(' ')} ({seller.role_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sellersQuery.isError && (
              <p className="text-sm text-red-600">No fue posible cargar los usuarios autorizados.</p>
            )}
            {!sellersQuery.isLoading && !sellersQuery.isError && sellersQuery.data?.length === 0 && (
              <p className="text-sm text-slate-500">No hay usuarios activos con acceso a esta sucursal.</p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!sellerId || mutation.isPending || sellersQuery.isError}
            >
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar vendedor
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
